/**
 * Generic handler for updating relationships between collections and thoughts
 * @param {Object} options - Configuration options
 * @param {string[]} options.selectedIds - IDs that are currently selected
 * @param {string[]} options.existingIds - IDs that were previously associated
 * @param {Function} options.addItemsFunction - Function to add items (collection or thoughts)
 * @param {Function} options.removeItemsFunction - Function to remove items (collection or thoughts)
 * @param {string} options.targetId - ID of the target entity (thought ID or collection ID)
 * @param {Function} options.setIsSubmitting - State setter for submission status
 * @param {Function} options.setSuccessMessage - State setter for success message
 * @param {Function} options.navigateWithFade - Navigation function
 * @param {string} options.navigateToPath - Path to navigate to after completion
 * @param {Object} options.navigationParams - Additional params for navigation
 * @param {string} options.itemType - Type of items being processed ('thought' or 'collection')
 * @returns {Promise<void>}
 */
export const handleRelationshipUpdate = async ({
  selectedIds,
  existingIds,
  addItemsFunction,
  removeItemsFunction,
  targetId,
  setIsSubmitting,
  setSuccessMessage,
  navigateWithFade,
  navigateToPath,
  navigationParams = {},
  itemType = 'item'
}) => {
  if (setIsSubmitting) {
    setIsSubmitting(true);
  }
  
  if (setSuccessMessage) {
    setSuccessMessage('');
  }
  
  try {
    // Calculate items to add and remove
    const itemsToAdd = selectedIds.filter(id => !existingIds.includes(id));
    const itemsToRemove = existingIds.filter(id => !selectedIds.includes(id));
    
    // Process additions
    if (itemsToAdd.length > 0) {
      await addItemsFunction(targetId, itemsToAdd);
    }
    
    // Process removals
    if (itemsToRemove.length > 0) {
      await removeItemsFunction(targetId, itemsToRemove);
    }
    
    // Show appropriate success message
    if (itemsToAdd.length > 0 || itemsToRemove.length > 0) {
      let message = '';
      if (itemsToAdd.length > 0) {
        message += `Added ${itemsToAdd.length > 1 ? itemsToAdd.length + ' ' : ''}${itemType}${itemsToAdd.length !== 1 ? 's' : ''}`;
      }
      if (itemsToRemove.length > 0) {
        if (message) message += '. ';
        message += `Removed ${itemsToRemove.length > 1 ? itemsToRemove.length + ' ' : ''}${itemType}${itemsToRemove.length !== 1 ? 's' : ''}`;
      }
      if (setSuccessMessage) setSuccessMessage(message);
    } else {
      if (setSuccessMessage) setSuccessMessage('No changes to save');
    }
    
    // Navigate back after a short delay
    if (navigateWithFade) {
      setTimeout(() => {
        navigateWithFade(navigateToPath, navigationParams);
      }, 1000);
    }
    
  } catch (error) {
    console.error(`Error updating ${itemType}s:`, error);
    if (setSuccessMessage) {
      setSuccessMessage(`Failed to update ${itemType}s. Please try again.`);
    }
  } finally {
    if (setIsSubmitting) {
      setIsSubmitting(false);
    }
  }
}; 